import {
  businessCommuterContactInformationParser,
  businessContactInformationParser,
} from '@oresundsbron/validators';
import { matchW } from 'fp-ts/lib/boolean';
import { pipe } from 'fp-ts/lib/function';
import { of } from 'fp-ts/lib/Task';
import {
  chain,
  fromPredicate,
  fromTask,
  getOrElseW,
  map,
} from 'fp-ts/lib/TaskOption';
import { Trans, useTranslation } from 'next-i18next';
import { FC, useCallback, useMemo, useState } from 'react';
import { useCountries } from '../../../hooks/useCountries';
import { useValidateCustomer } from '~/hooks/useValidateCustomer';
import { usePhoneCodes } from '../../../hooks/usePhoneCodes';
import { useValidatePhone } from '../../../hooks/useValidatePhone';
import { getDefaultCountry } from '../../../lib/countries';
import { pick } from '../../../lib/data';
import { toInputDate } from '../../../lib/dates';
import { chainValidations, isFieldErrorEmpty } from '../../../lib/errors';
import { validateT } from '../../../lib/zod';
import {
  BusinessContactInformation,
  Data,
} from '../../../components/Forms/ContactInformation/Business';
import { Typography } from '@oresundsbron/bridge-ui';
import { tapTO } from '@oresundsbron/utilities';
import { ValidationResult } from '@oresundsbron/use-form';
import { useRouter } from 'next/router';
import { SignUpFormPart } from '../utils';
import { useVerifyCustomer } from '~/hooks/useVerifyCustomer';

const pickFromBusinessInformation = pick<Data>();
const pickFromContact = pick<Data['contact']>();

const pickPhone = pickFromBusinessInformation('country', 'phoneCode', 'phone');

const pickCustomerData = pickFromBusinessInformation('vatno');
const pickEmailData = pickFromContact('email');

const pickContactPhone = (data: Data) =>
  pipe(
    data,
    pickFromBusinessInformation('contact'),
    ({ contact }) => contact,
    pickFromContact('phoneCode', 'phone'),
    ({ phoneCode, phone }) => ({
      phoneCode,
      phone,
      ...pickFromBusinessInformation('country')(data),
    })
  );

export const ContactInformation: FC<
  SignUpFormPart & { commuter?: boolean }
> = ({ namespace, titleTranslationKey, commuter, ns }) => {
  const validatePhone = useValidatePhone();
  const validateCustomer = useValidateCustomer();
  const countries = useCountries();
  const phoneCodes = usePhoneCodes();
  const { locale } = useRouter();
  const [vatnoExist, toggleVatnoExist] = useState(false);
  const verifyCustomer = useVerifyCustomer();

  const { t } = useTranslation(ns, {
    nsMode: 'fallback',
  });

  const pickAndValidatePhone = useCallback(
    (data: Data) => (errors?: ValidationResult<Data>) =>
      pipe(
        errors,
        fromPredicate(isFieldErrorEmpty('phone')),
        map(() => data),
        map(pickPhone),
        chain(
          validatePhone<Data>({
            phone: t('form.validations.phone.invalid'),
          })
        )
      ),
    [validatePhone, t]
  );

  const pickAndValidateContactPhone = useCallback(
    (data: Data) => (errors?: ValidationResult<Data>) =>
      pipe(
        errors,
        fromPredicate(isFieldErrorEmpty('contact.phone')),
        map(() => data),
        map(pickContactPhone),
        chain(
          validatePhone<Data>({
            'contact.phone': t('form.validations.phone.invalid'),
          })
        )
      ),
    [validatePhone, t]
  );

  const pickAndValidateCustomer = useCallback(
    (data: Data) => (errors?: ValidationResult<Data>) =>
      pipe(
        errors,
        fromPredicate(isFieldErrorEmpty('contact.phone')),
        map(() => data),
        map(pickCustomerData),
        chain(
          validateCustomer<Data>((res) => ({
            vatno: res.vatno ? t('form.validations.vatno.exist') : undefined,
          }))
        ),
        tapTO((res) =>
          res.vatno && !commuter !== undefined
            ? toggleVatnoExist(true)
            : toggleVatnoExist(false)
        ),
        map((res) => ({
          ...res,
          vatno: data.bypassVatno === 'true' ? undefined : res.vatno,
        }))
      ),
    [validateCustomer, t, commuter]
  );

  const validate = useCallback(
    (data: Data) =>
      pipe(
        data,
        validateT(
          commuter
            ? businessCommuterContactInformationParser.spa
            : businessContactInformationParser.spa,
          t
        ),
        fromTask,
        chainValidations(
          pickAndValidatePhone(data),
          pickAndValidateContactPhone(data),
          pickAndValidateCustomer(data)
        ),
        getOrElseW(() => of(undefined))
      )(),
    [
      t,
      pickAndValidateContactPhone,
      pickAndValidateCustomer,
      pickAndValidatePhone,
      commuter,
    ]
  );

  const pickAndVerifyCustomer = useCallback(
    (data: Data) => async (errors?: ValidationResult<Data['contact']>) => {
      const pickedData = pickEmailData(data.contact);

      const verifyCustomerFn = verifyCustomer<Data['contact']>((res) => ({
        email:
          !res.success && isFieldErrorEmpty('email')(errors)
            ? t('form.validations.email.exist')
            : undefined,
      }));
      const result = await verifyCustomerFn(pickedData);
      const values = Object.values(result);
      for (const value of values) {
        if (value) {
          return result;
        }
      }
      return undefined;
    },
    [verifyCustomer, t]
  );

  const validateAndVerify = useCallback(
    async (data: Data) => {
      const useAuth0 = process.env.NEXT_PUBLIC_USE_AUTH0 === 'true';

      const validation = await validate(data);
      if (!useAuth0 || validation) {
        return validation;
      }
      const verification = await pickAndVerifyCustomer(data)();
      return verification;
    },
    [validate, pickAndVerifyCustomer]
  );

  const startDate = useMemo(
    () =>
      pipe(
        !!commuter,
        matchW(
          () => undefined,
          () => toInputDate(new Date())
        )
      ),
    [commuter]
  );

  const phoneCode = phoneCodes.find(
    (c) => c.country === getDefaultCountry(locale || '')
  );

  return (
    <BusinessContactInformation
      namespace={namespace}
      title={t(titleTranslationKey)}
      defaultValues={{
        country: getDefaultCountry(locale || ''),
        phoneCode: phoneCode?.value,
        contact: {
          phoneCode: phoneCode?.value,
        },
        commuter: { startDate },
      }}
      vatnoExist={vatnoExist}
      labels={{
        companyName: t('form.labels.companyName'),
        vatno: t('form.labels.vatno'),
        ean: t('form.labels.ean'),
        city: t('form.labels.city'),
        address: t('form.labels.address'),
        zipCode: t('form.labels.zipCode'),
        phone: t('form.labels.phone'),
        phoneCode: t('form.labels.phoneCode'),
        country: t('form.labels.country'),
        contact: {
          firstName: t('form.labels.firstName'),
          lastName: t('form.labels.lastName'),
          phoneCode: t('form.labels.phoneCode'),
          email: t('form.labels.email'),
          phone: t('form.labels.phone'),
        },
        commuter: {
          email: t('form.labels.email'),
          firstName: t('form.labels.firstName'),
          lastName: t('form.labels.lastName'),
          startDate: t('form.labels.startDate'),
        },
        commuterLabel: t('form.labels.commuter'),
        contactPersonLabel: t('form.labels.contactPerson'),
        loading: t('action.loading'),
        bypassVatno: t('form.labels.bypassVatno'),
        startDateDescription: t('form.descriptions.startDate'),
      }}
      disclaimers={{
        vatno: (
          <Trans
            i18nKey="form.sections.contactInformation.disclaimers.vatno"
            ns={ns}
          >
            <Typography />
          </Trans>
        ),
        vatnoCommuter: (
          <Trans
            i18nKey="form.sections.contactInformation.disclaimers.vatnoCommuter"
            ns={ns}
          >
            <Typography />
          </Trans>
        ),
      }}
      placeholders={{
        country: t('form.placeholders.country'),
        phoneCode: t('form.placeholders.phoneCode'),
      }}
      countryOptions={countries}
      phoneCodeOptions={phoneCodes}
      onValidate={validateAndVerify}
      commuter={commuter}
    />
  );
};
